import React from 'react';
import { buildPackageShareMessage } from '../utils/shareMessages';

// Official WhatsApp glyph so the button reads as "WhatsApp" instantly (lucide's
// MessageCircle looked too generic for a share pill).
const WA_GLYPH = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .18 5.32.18 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.86 11.86 0 0 0 5.72 1.46h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.15-3.43-8.44ZM12.07 21.5h-.01a9.6 9.6 0 0 1-4.9-1.35l-.35-.21-3.76.98 1-3.67-.23-.38a9.63 9.63 0 0 1-1.48-5.11c0-5.32 4.34-9.65 9.68-9.65 2.58 0 5.01 1 6.83 2.83a9.6 9.6 0 0 1 2.83 6.83c0 5.34-4.32 9.73-9.61 9.73Zm5.28-7.24c-.29-.14-1.71-.84-1.97-.94-.27-.1-.47-.14-.66.15-.19.29-.75.94-.92 1.14-.17.19-.34.22-.63.07-.29-.14-1.23-.45-2.34-1.44a8.76 8.76 0 0 1-1.62-2.02c-.17-.29-.02-.44.13-.59.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.14-.66-1.58-.9-2.17-.24-.58-.48-.5-.66-.51l-.56-.01c-.19 0-.51.07-.78.36-.27.29-1.02.99-1.02 2.42s1.05 2.81 1.2 3c.15.19 2.07 3.16 5.02 4.43.7.3 1.24.48 1.67.61.7.22 1.34.19 1.85.12.56-.08 1.71-.7 1.95-1.37.24-.67.24-1.24.17-1.37-.07-.13-.26-.19-.55-.33Z"/>
  </svg>
);

/**
 * Small circular icon-only WhatsApp share button used on package cards.
 * Full-text 'inline' variant used on the package detail page.
 * Props: pkg (package), variant: 'card' | 'inline'
 */
const buildLink = (pkg) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const code = pkg?.pkg_code || pkg?.id || '';
  // Use the server-rendered share URL so WhatsApp / social crawlers see the
  // package's og:title / og:description / og:image before redirecting real users
  // to /package/<code>.
  const url = `${origin}/api/share/package/${code}`;
  const text = buildPackageShareMessage(pkg, url);
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

const WhatsAppShareBtn = ({ pkg, variant = 'card', className = '' }) => {
  if (!pkg) return null;
  const isInline = variant === 'inline';
  const isPill = variant === 'pill';
  const isCircleText = variant === 'circle-text';

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const href = buildLink(pkg);
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  if (isCircleText) {
    // Detail-page CTA: a fat green circular icon with "Share on WhatsApp"
    // label next to it. Designed to feel like a primary action, not a chip.
    return (
      <button
        onClick={onClick}
        data-testid={`wa-share-detail-${pkg.pkg_code || pkg.id}`}
        className={`group inline-flex items-center gap-3 hover:opacity-95 transition ${className}`}
        title="Share this deal on WhatsApp"
      >
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#25D366] group-hover:bg-[#1ebe5b] text-white shadow-md transition">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .18 5.32.18 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.86 11.86 0 0 0 5.72 1.46h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.15-3.43-8.44ZM12.07 21.5h-.01a9.6 9.6 0 0 1-4.9-1.35l-.35-.21-3.76.98 1-3.67-.23-.38a9.63 9.63 0 0 1-1.48-5.11c0-5.32 4.34-9.65 9.68-9.65 2.58 0 5.01 1 6.83 2.83a9.6 9.6 0 0 1 2.83 6.83c0 5.34-4.32 9.73-9.61 9.73Zm5.28-7.24c-.29-.14-1.71-.84-1.97-.94-.27-.1-.47-.14-.66.15-.19.29-.75.94-.92 1.14-.17.19-.34.22-.63.07-.29-.14-1.23-.45-2.34-1.44a8.76 8.76 0 0 1-1.62-2.02c-.17-.29-.02-.44.13-.59.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.14-.66-1.58-.9-2.17-.24-.58-.48-.5-.66-.51l-.56-.01c-.19 0-.51.07-.78.36-.27.29-1.02.99-1.02 2.42s1.05 2.81 1.2 3c.15.19 2.07 3.16 5.02 4.43.7.3 1.24.48 1.67.61.7.22 1.34.19 1.85.12.56-.08 1.71-.7 1.95-1.37.24-.67.24-1.24.17-1.37-.07-.13-.26-.19-.55-.33Z"/>
          </svg>
        </span>
        <span className="text-[15px] font-semibold text-[#128c4d] group-hover:text-[#0b6c3a]">
          Share on WhatsApp
        </span>
      </button>
    );
  }

  if (isPill) {
    return (
      <button
        onClick={onClick}
        data-testid={`wa-share-${pkg.pkg_code || pkg.id}`}
        className={`inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm transition ${className}`}
        title="Share this deal on WhatsApp"
      >
        {WA_GLYPH} Share
      </button>
    );
  }

  if (isInline) {
    return (
      <button
        onClick={onClick}
        data-testid={`wa-share-${pkg.pkg_code || pkg.id}`}
        className={`inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition ${className}`}
        title="Share this deal on WhatsApp"
      >
        {WA_GLYPH} Share on WhatsApp
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      data-testid={`wa-share-${pkg.pkg_code || pkg.id}`}
      aria-label="Share this deal on WhatsApp"
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white shadow-sm transition ${className}`}
      title="Share this deal on WhatsApp"
    >
      {WA_GLYPH}
    </button>
  );
};

export default WhatsAppShareBtn;
